import React, { useCallback, useEffect, useState } from "react";
import { useApi } from "../../../shared/api/api";
import AlertMessage from "../../../shared/components/AlertMessage";
import Modal from "../../../shared/components/Modal";
import PaginationToolbar from "../../../shared/components/PaginationToolbar";
import { useAutoDismissMessage } from "../../../shared/hooks/useAutoDismissMessage";
import { formatCRC } from "../../../shared/utils/format";
import { crearTipoCitaVacio } from "../../../dominio/tipoCita";

const TiposCita = React.forwardRef(function TiposCita({ embedded = false, refreshKey = 0 }, ref) {
  const { apiFetch } = useApi();
  const [tipos, setTipos] = useState([]);
  const [form, setForm] = useState(crearTipoCitaVacio);
  const [editMode, setEditMode] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [confirmSave, setConfirmSave] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({ visible: false, id: null, nombre: "" });
  const [paginaActual, setPaginaActual] = useState(1);
  const [registrosPorPagina, setRegistrosPorPagina] = useState(5);
  const [busqueda, setBusqueda] = useState("");
  const [cargando, setCargando] = useState(false);
  const totalColumnas = embedded ? 4 : 5;

  useAutoDismissMessage(mensaje, () => setMensaje(null));

  const listar = useCallback(async () => {
    setCargando(true);

    try {
      const data = await apiFetch("/tiposcita.php?action=list");
      setTipos(data || []);
      setPaginaActual(1);
    } finally {
      setCargando(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    listar();
  }, [listar, refreshKey]);

  const tiposFiltrados = tipos.filter((t) => {
    const textoBusqueda = busqueda.toLowerCase();
    return (
      String(t.Nombre || "").toLowerCase().includes(textoBusqueda) ||
      String(t.MontoASumar || "").includes(busqueda) ||
      String(t.Descuento || "").includes(busqueda)
    );
  });

  const totalPaginas = Math.ceil(tiposFiltrados.length / registrosPorPagina);
  const indiceInicio = (paginaActual - 1) * registrosPorPagina;
  const tiposPaginados = tiposFiltrados.slice(indiceInicio, indiceInicio + registrosPorPagina);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const ejecutarGuardado = async () => {
    let resp;
    if (editMode) {
      resp = await apiFetch("/tiposcita.php?action=update", "PUT", form);
    } else {
      resp = await apiFetch("/tiposcita.php?action=add", "POST", form);
    }

    if (resp?.ok) {
      setMensaje({ tipo: "success", texto: editMode ? "Tipo de cita actualizado" : "Tipo de cita agregado" });
      resetForm();
      setShowFormModal(false);
      listar();
    } else {
      setMensaje({ tipo: "danger", texto: resp?.error || "Ocurrio un error" });
    }

    setConfirmSave(false);
  };

  const guardar = async (e) => {
    e.preventDefault();
    if (editMode) {
      setConfirmSave(true);
      return;
    }

    await ejecutarGuardado();
  };

  const abrirModalAgregar = () => {
    resetForm();
    setShowFormModal(true);
  };

  React.useImperativeHandle(ref, () => ({
    abrirModalAgregar
  }));

  const editar = (t) => {
    setForm(t);
    setEditMode(true);
    setShowFormModal(true);
  };

  const confirmarEliminar = (id, nombre) => {
    setConfirmDelete({ visible: true, id, nombre });
  };

  const cancelarEliminar = () => {
    setConfirmDelete({ visible: false, id: null, nombre: "" });
  };

  const eliminar = async () => {
    if (!confirmDelete.id) return;

    const resp = await apiFetch("/tiposcita.php?action=delete", "POST", { IdTiposCita: confirmDelete.id });
    if (resp?.ok) {
      setMensaje({ tipo: "success", texto: "Tipo de cita eliminado correctamente" });
      cancelarEliminar();
      listar();
    } else {
      setMensaje({ tipo: "danger", texto: resp?.error || "Error al eliminar" });
    }
  };

  const resetForm = () => {
    setForm(crearTipoCitaVacio());
    setEditMode(false);
  };

  return (
    <div className={embedded ? "admin-embedded-section" : "container-fluid mt-3 px-3 px-md-4"}>
      {!embedded && <h2>Gestion de Tipos de Cita</h2>}

      {!embedded && (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Tipos de cita</h4>
          <button type="button" className="btn btn-success btn-sm" onClick={abrirModalAgregar}>
            Agregar tipo
          </button>
        </div>
      )}

      <AlertMessage message={mensaje} onClose={() => setMensaje(null)} />

      <div className="card mb-3">
        <div className="card-body">
          <input
            type="text"
            className="form-control"
            placeholder="Buscar por nombre, monto o descuento..."
            value={busqueda}
            onChange={(e) => {
              setBusqueda(e.target.value);
              setPaginaActual(1);
            }}
          />
        </div>
      </div>

      <div className="card edge-to-edge-table-card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped table-sm align-middle mobile-compact-table appointment-types-table">
              <thead className="table-dark">
                <tr>
                  {!embedded && <th>ID</th>}
                  <th>Nombre</th>
                  <th className="d-none d-md-table-cell">Monto a Sumar</th>
                  <th>Descuento</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {cargando && (
                  <tr>
                    <td colSpan={totalColumnas} className="text-center py-4">
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Cargando tipos de cita...
                    </td>
                  </tr>
                )}
                {!cargando && tiposPaginados.map((t) => (
                  <tr key={t.IdTiposCita}>
                    {!embedded && <td className="fw-bold">{t.IdTiposCita}</td>}
                    <td className="text-truncate" title={t.Nombre}>{t.Nombre}</td>
                    <td className="d-none d-md-table-cell">{formatCRC(t.MontoASumar)}</td>
                    <td>{Number(t.Descuento) || 0}%</td>
                    <td>
                      <div className="btn-group btn-group-sm table-row-actions" role="group" aria-label="Editar o eliminar tipo de cita">
                        <button className="btn btn-primary" title="Editar tipo de cita" onClick={() => editar(t)}>✎</button>
                        <button className="btn btn-danger" title="Eliminar tipo de cita" onClick={() => confirmarEliminar(t.IdTiposCita, t.Nombre)}>✕</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!cargando && tipos.length === 0 && <tr><td colSpan={totalColumnas} className="text-center">No hay tipos de cita</td></tr>}
                {!cargando && tipos.length > 0 && tiposFiltrados.length === 0 && <tr><td colSpan={totalColumnas} className="text-center">No se encontraron resultados para la busqueda</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <PaginationToolbar
        page={paginaActual}
        totalPages={totalPaginas}
        pageSize={registrosPorPagina}
        disabled={cargando}
        onPageChange={setPaginaActual}
        onPageSizeChange={(size) => {
          setRegistrosPorPagina(size);
          setPaginaActual(1);
        }}
      />

      {showFormModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editMode ? "Editar tipo de cita" : "Agregar tipo de cita"}</h5>
                <button type="button" className="btn-close" onClick={() => setShowFormModal(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={guardar} className="row g-2">
                  <div className="col-12 col-md-6">
                    <label className="form-label">Nombre</label>
                    <input type="text" name="Nombre" className="form-control" value={form.Nombre} onChange={handleChange} required />
                  </div>
                  <div className="col-12 col-md-3">
                    <label className="form-label">Monto a Sumar</label>
                    <input type="number" name="MontoASumar" className="form-control" value={form.MontoASumar} onChange={handleChange} required />
                  </div>
                  <div className="col-12 col-md-3">
                    <label className="form-label">Descuento (%)</label>
                    <input type="number" name="Descuento" className="form-control" value={form.Descuento} onChange={handleChange} />
                  </div>
                  <div className="col-12 d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowFormModal(false)}>Cerrar</button>
                    <button type="submit" className="btn btn-success btn-sm">{editMode ? "Actualizar" : "Agregar"}</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmSave && (
        <Modal
          title="Confirmar cambio"
          onClose={() => setConfirmSave(false)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmSave(false)}>Cancelar</button>
              <button type="button" className="btn btn-success" onClick={ejecutarGuardado}>Actualizar</button>
            </>
          }
        >
          <p>Deseas aplicar los cambios a este tipo de cita?</p>
        </Modal>
      )}

      {confirmDelete.visible && (
        <Modal
          title="Confirmar eliminacion"
          onClose={cancelarEliminar}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={cancelarEliminar}>Cancelar</button>
              <button type="button" className="btn btn-danger" onClick={eliminar}>Eliminar</button>
            </>
          }
        >
          <p>Estas seguro que quieres eliminar el tipo de cita <strong>{confirmDelete.nombre}</strong>?</p>
        </Modal>
      )}
    </div>
  );
});

export default TiposCita;
